import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomValidators } from 'ng2-validation';
import { ToastrService } from 'ngx-toastr';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { CplRequestItem } from '../../../model/cpl-request-item.model';
import { CplUserRequest } from '../../../model/cpl-user-request.model';
import { RequestReopenStageId } from '../../../model/enum/request-reopen-stage-ids.enum';
import { RequestUserStatus } from '../../../model/enum/request-user-status.enum';
import { DateHelpers, User, UserService } from '@edl-development/edl-common';
import { ReqVerificationResponse } from '../../../model/req-verification-response.model';
import { ReqVerificationStage } from '../../../model/req-verification-stage.model';
import { UserRequestVerification } from '../../../model/request/user-request-verification-action.model';
import { RequestHelpersService } from '../../../services/request-helpers/request-helpers.service';
import { RequestVerifyService } from '../../../services/request-verify.service';
import { TargetEvaluator } from '../../enums/target-evaluator.enum';
import { RequestVerificationStatus } from '../../enums/verification-status.enum';

@Component({
    templateUrl: './verify-request-modal.component.html',
    styleUrls: ['./verify-request-modal.component.scss']
})
export class VerifyRequestModalComponent implements OnInit {
    @Input() userRequest: CplUserRequest;
    @Input() userRequestActions: Array<CplUserRequest>;
    @Input() requestItem: CplRequestItem;
    @Input() action: RequestVerificationStatus;
    @Input() canClose: boolean = true;

    @Output() onSubmissionComplete = new EventEmitter<boolean>();

    RequestReopenStageId = RequestReopenStageId;
    RequestUserStatus = RequestUserStatus;
    RequestVerificationStatus = RequestVerificationStatus;
    TargetEvaluator = TargetEvaluator;

    form: UntypedFormGroup;
    ReloadParent: boolean;

    currentStage: ReqVerificationStage;
    reopenStage: ReqVerificationStage;
    reopenStages: ReqVerificationStage[];
    reopenFollowupStages: ReqVerificationStage[];

    shouldDisplayReopenWarning: boolean;
    busy: boolean;
    user: User;

    constructor(
        public activeModal: NgbActiveModal, 
        private formBuilder: UntypedFormBuilder,
        public requestHelpersService : RequestHelpersService,
        private requestVerifyService: RequestVerifyService,
        private toastr: ToastrService,
        private userService: UserService,
        private a48HelpersService: A48HelpersService
    ) {}


    ngOnInit() {
        this.ReloadParent = false;
        this.shouldDisplayReopenWarning = false;
        this.busy = true;

        if (this.isBulkAction()) {
            this.userRequest = this.userRequestActions[0];
            this.reopenStages = this.requestItem.VerificationStages.slice(0, 1);
            this.reopenFollowupStages = this.requestItem.FollowupRequired ? this.requestItem.FollowupVerificationStages.slice(0, 1) : [];

        } else {
            this.currentStage = this.userRequest.VerificationStage ? this.userRequest.VerificationStages.find(stage => stage.StageID == this.userRequest.VerificationStage.StageID) : null;
            this.reopenStages = this.getReopenStages(this.userRequest, this.requestItem, 0);
            this.reopenFollowupStages = this.getReopenStages(this.userRequest, this.requestItem, 1);
        }

        if (!this.isBulkAction()) {
            this.userService.getAccountByUserId(this.userRequest.UserID)
                .subscribe((result: User) =>  {
                    this.user = result;
                    this.busy = false;
                }, () => {
                    this.busy = false
                })
        } else {
            this.busy = false;
        }

        const followUpDueDate = this.isBulkAction() ? DateHelpers.shortDateStringToDate(this.requestItem.DefaultFollowupDueDate) : this.converFollowUpDueDate(this.userRequest.FollowupDueDate);

        let verificationAction = this.userRequest.VerificationStage ? this.userRequest.VerificationStage.VerificationStatus : this.action ? this.action : RequestVerificationStatus.REOPEN;
        if (verificationAction == RequestVerificationStatus.PENDING)
            verificationAction = this.action ? this.action : RequestVerificationStatus.APPROVED;

        this.form = this.formBuilder.group({
            VerificationStatus: [verificationAction, [Validators.required]],
            ReopenStageId: [null, []],
            FollowupDueDate: [followUpDueDate, this.getFollowupDueDateValidation(verificationAction)],
            VerificationStages: this.formBuilder.group({
                Stages: this.setFormVerificationStages(this.requestItem.VerificationStages, this.userRequest.VerificationStages)
            }),
            FollowupVerificationStages: this.formBuilder.group({
                Stages: this.setFormVerificationStages(this.requestItem.FollowupVerificationStages, this.userRequest.VerificationStages)
            }),
            UserNote: [null, []],
            SupervisorNote: [null, []],
            ShouldReserveSeat: [true, [Validators.required]],
            ShouldSubmitToPerms: [this.userRequest?.Act48UserSubmissionDetails?.ShouldSubmitToPerms ?? true, []],
            ShouldSendNotifications: [true, [Validators.required]]
        });

        this.form.get('VerificationStatus').valueChanges.subscribe(this.verificationStatusSubscription);
        this.form.get('ReopenStageId').valueChanges.subscribe(this.reopenStageIdSubscription);
        this.form.get('VerificationStatus').updateValueAndValidity();
        this.form.get('VerificationStatus').setValue(this.action);
    }

    ok({ value: { 
            VerificationStatus, 
            ReopenStageId, 
            FollowupDueDate, 
            VerificationStages, 
            FollowupVerificationStages, 
            UserNote, 
            SupervisorNote,
            ShouldReserveSeat,
            ShouldSubmitToPerms,
            ShouldSendNotifications }, valid } : {
        value: {
            VerificationStatus: number;
            ReopenStageId: string;
            FollowupDueDate: Date;
            VerificationStages: { Stages: ReqVerificationStage[] };
            FollowupVerificationStages: { Stages: ReqVerificationStage[] };
            UserNote: string;
            SupervisorNote: string;
            ShouldReserveSeat: boolean;
            ShouldSubmitToPerms: boolean;
            ShouldSendNotifications: boolean;
        }; valid: boolean }) {
        if (valid) {
            this.busy = true;

            const action: UserRequestVerification[] = new Array<UserRequestVerification>();
            if (this.isBulkAction()) {
                this.userRequestActions.map((userRequest: CplUserRequest) => {
                    const requestVerification = this.createUserRequestVerification({
                        userRequest,
                        VerificationStatus,
                        ReopenStageId,
                        FollowupDueDate: this.requestHelpersService.convertFollowupDueDateToLocal(
                            this.converFollowUpDueDate(userRequest.FollowupDueDate) ? userRequest.FollowupDueDate  : FollowupDueDate
                        ),
                        VerificationStages: userRequest.VerificationStages,
                        UserNote,
                        SupervisorNote,
                        ShouldSubmitToPerms: true,
                        ShouldSendNotifications,
                        ShouldReserveSeat
                    });

                    action.push(requestVerification);
                });

                this.requestVerifyService.bulkVerify(action)
                    .subscribe((result: ReqVerificationResponse[]) => {
                        this.busy = false;

                        this.onSubmissionComplete.emit(true);
                        if (this.canClose) {
                            this.activeModal.close({reloadParent: this.ReloadParent, result});
                        }
                        return result;
                    }, (error) => {
                        this.busy = false;
                    });
            } else {
                const requestVerification = this.createUserRequestVerification({
                    userRequest: this.userRequest,
                    VerificationStatus,
                    ReopenStageId,
                    FollowupDueDate,
                    VerificationStages: VerificationStages.Stages.concat(FollowupVerificationStages.Stages),
                    UserNote,
                    SupervisorNote,
                    ShouldReserveSeat,
                    ShouldSubmitToPerms,
                    ShouldSendNotifications
                });

                this.requestVerifyService.verify(requestVerification)
                    .subscribe((result) => {
                        this.busy = false;
                        const successMessage = this.getVerificationSuccessMessage(VerificationStatus);
                        this.toastr.success(successMessage);
                        
                        this.onSubmissionComplete.emit(true);
                        if (this.canClose) {
                            this.activeModal.close(this.ReloadParent);
                        }
                    }, (error) => {
                        this.busy = false;
                    });
            }
        }
    }

    cancel() {
        this.activeModal.dismiss();
    }

    updateDueDateValidator() {
        const verificationStatus = this.form.get('VerificationStatus').value;
        this.form.get('FollowupDueDate').setValidators(this.getFollowupDueDateValidation(verificationStatus));
        this.form.get('FollowupDueDate').updateValueAndValidity();
    }

    isBulkAction = () => this.userRequestActions != null && this.userRequestActions.length > 0;

    getVerificationSuccessMessage(verificationStatus: RequestVerificationStatus) {
        switch(verificationStatus) {
            case RequestVerificationStatus.APPROVED:
                return 'User request successfully approved.';
            case RequestVerificationStatus.DISAPPROVED:
                return 'User request successfully disapproved.';
            case RequestVerificationStatus.CANCELLED:
                return 'User Request successfully cancelled.';
            case RequestVerificationStatus.REOPEN:
                return 'User request successfully reopened.'
            case RequestVerificationStatus.FORGIVEN:
                return 'Follow-Up successfully forgiven.';
            case RequestVerificationStatus.SKIPPED:
                return 'User request successfully bypassed'
            default:
                return 'User request completed successfully.';
        }     
    }

    shouldDisplayReopenFollowup() {
        return (this.requestItem.FollowupRequired || (this.reopenFollowupStages && this.reopenFollowupStages.length > 0)) &&
            (this.isBulkAction() || !DateHelpers.isMinDate(this.userRequest.ApprovedDate))
    }

    shouldDisplayFollowUpSection(verificationStatus: RequestVerificationStatus) {
        return verificationStatus &&
            verificationStatus !== RequestVerificationStatus.DISAPPROVED &&
            verificationStatus !== RequestVerificationStatus.CANCELLED &&
            verificationStatus !== RequestVerificationStatus.FORGIVEN &&
            (verificationStatus !== RequestVerificationStatus.REOPEN || this.reopenStage?.Followup !== 1) && 
            this.requestItem.FollowupRequired &&
            (this.isBulkAction() || (this.userRequest.UserStatus != RequestUserStatus.FollowupSubmitted))
    }

    public canDisplayNotificationsToggle() {
        const status = this.form.get('VerificationStatus').value;

        return !(this.requestItem?.Act48Detail?.Applicable ?? false) || 
        (
            status !== RequestVerificationStatus.FORGIVEN && 
            (this.isBulkAction() || !this.isFinalApproval())
        );
    }

    public canDisplaySubmitHoursToggle() {
        const status = this.form.get('VerificationStatus').value;
        return !this.isBulkAction() && this.user &&
            this.a48HelpersService.canDisplaySubmitHoursToggle({
                hasAct48: this.requestItem?.Act48Detail?.Applicable ?? false,
                isUserPara: this.userRequest.Act48UserSubmissionDetails?.IsPara,
                hasPPID: !!this.user?.PPID,
                excludeFromSubmission: this.user?.ExcludeFromSubmission,
                contextCode: this.userRequest.Act48UserSubmissionDetails.ContextCode
            }) && (status == RequestVerificationStatus.APPROVED || status === RequestVerificationStatus.FORGIVEN || status == RequestVerificationStatus.SKIPPED);
    }

    public canDisplayShouldReserveSeat() {
        const status = this.form.get('VerificationStatus').value;
        const reopenStageId = this.form?.get('ReopenStageId').value;
        return this.requestItem?.RosterPreference?.HasSeatLimit && 
            status === RequestVerificationStatus.REOPEN && 
            reopenStageId === RequestReopenStageId.ReopenToUserStageId;
    }

    private verificationStatusSubscription = (verificationStatus: RequestVerificationStatus) => {
        if (verificationStatus === RequestVerificationStatus.REOPEN) {
            this.form.get('ReopenStageId').setValidators([Validators.required]);
        } else {
            this.form.get('ReopenStageId').setValidators([]);
        }
        this.form.get('ReopenStageId').updateValueAndValidity();
        this.updateDueDateValidator();
    }

    private reopenStageIdSubscription = (reopenStageId: string) => {
        this.shouldDisplayReopenWarning = this.shouldDisplayReopenFollowup() &&
            this.isBulkAction() &&
            (reopenStageId === RequestReopenStageId.ReopenToUserStageId ||
                this.reopenStages && this.reopenStages.map(stage => stage.StageID).indexOf(reopenStageId) !== -1);

        this.reopenStage = this.requestItem.VerificationStages
            .concat(this.requestItem.FollowupVerificationStages)
            .find(stage => stage.StageID === reopenStageId);


        this.setResetStageSelectedUser((<UntypedFormArray>this.form.get(['VerificationStages', 'Stages'])), reopenStageId);
        this.setResetStageSelectedUser((<UntypedFormArray>this.form.get(['FollowupVerificationStages', 'Stages'])), reopenStageId);
        this.updateDueDateValidator();
    }

    private setResetStageSelectedUser = (stages: UntypedFormArray, reopenStageId: string) => {
        stages.controls.forEach((verificationStage) => {
            if (verificationStage.get('StageID').value === reopenStageId && verificationStage.get('SelectedUserId').value === TargetEvaluator.SKIP) {
                verificationStage.get('SelectedUserId').setValue(TargetEvaluator.ALL);
            }
        });
    }

    private setFormVerificationStages(requestItemVerificationStages: ReqVerificationStage[], userRequestVerificationStages: ReqVerificationStage[]) {
        const verificationStagesArray = this.formBuilder.array([]);
        requestItemVerificationStages.forEach((verificationStage: ReqVerificationStage) => {
            const SelectedUserId = userRequestVerificationStages.find(v => v.StageID === verificationStage.StageID)?.SelectedUserId ?? TargetEvaluator.ALL;
            verificationStagesArray.push(this.formBuilder.group({
                Followup: [verificationStage.Followup, []],
                Order: [verificationStage.Order, []],
                StageID: [verificationStage.StageID, []],
                SelectedUserId: [SelectedUserId, []]
            }));
        })

        return verificationStagesArray;
    }

    private getReopenStages(userRequest: CplUserRequest, requestItem: CplRequestItem, followup: number) {
        if (followup === 1 ?
            (userRequest.FollowupVersion < requestItem.FollowupVersion ||
                DateHelpers.isMinDate(userRequest.FollowupSubmissionDate)) :
            (userRequest.Version < requestItem.Version ||
                DateHelpers.isMinDate(userRequest.SubmissionDate))) {
            return [];
        }

        if (this.requestHelpersService.isFinalUserStatus(userRequest.UserStatus)) {
            return (followup == 1 ? this.requestItem.FollowupVerificationStages : this.requestItem.VerificationStages)
                .sort((stage1, stage2) => stage1.Order - stage2.Order)

        } else {
            return this.userRequest.VerificationStages
                .filter((stage) => stage.Followup === followup && stage.VerificationStatus !== RequestVerificationStatus.NOACTION)
                .sort((stage1, stage2) => stage1.Order - stage2.Order);
        }
    }

    private createUserRequestVerification({ 
        userRequest, 
        VerificationStatus, 
        ReopenStageId, 
        FollowupDueDate, 
        VerificationStages,
        UserNote, 
        SupervisorNote,
        ShouldReserveSeat,
        ShouldSendNotifications,
        ShouldSubmitToPerms = true 
    } :{ 
            userRequest: CplUserRequest;
            VerificationStatus: number;
            ReopenStageId: string;
            FollowupDueDate: Date;
            VerificationStages: ReqVerificationStage[];
            UserNote: string;
            SupervisorNote: string;
            ShouldReserveSeat: boolean;
            ShouldSendNotifications?: boolean;
            ShouldSubmitToPerms?: boolean;
    }) {
        const requestVerification = new UserRequestVerification();
        requestVerification.RefId = userRequest.UserRequestID;
        requestVerification.VerificationID = userRequest.VerificationStage ? userRequest.VerificationStage.VerificationID : '0'
        requestVerification.StageID = userRequest.VerificationStage ? userRequest.VerificationStage.StageID : '0'
        requestVerification.UserCycleID = userRequest.VerificationStage ? userRequest.VerificationStage.RefId : userRequest.RequestID;
        requestVerification.VerificationStatus = VerificationStatus;
        requestVerification.ReopenStageId = ReopenStageId ? ReopenStageId : '';
        requestVerification.VerifiedBy = userRequest.VerificationStage ? userRequest.VerificationStage.VerifiedBy : '0'
        requestVerification.VerifiedByName = userRequest.VerificationStage ? userRequest.VerificationStage.VerifiedByName : '';
        requestVerification.VerifiedDate = userRequest.VerificationStage ? userRequest.VerificationStage.VerifiedDate : new Date();
        requestVerification.FollowupDueDate = this.converFollowUpDueDate(FollowupDueDate);
        requestVerification.NotifyUserOfComment = false;
        requestVerification.UserNote = UserNote;
        requestVerification.SupervisorNote = SupervisorNote;
        requestVerification.VerificationStages = VerificationStages;
        requestVerification.ShouldSendNotifications = ShouldSendNotifications ?? true;
        requestVerification.ShouldReserveSeat = ShouldReserveSeat;

        //Used by Bulk Actions only for displaying results
        requestVerification.UserRequestCreatorFirstName = userRequest.FirstName;
        requestVerification.UserRequestCreatorLastName = userRequest.LastName;
        requestVerification.UserRequestDescription = userRequest.Description;
        requestVerification.ShouldSubmitToPerms = ShouldSubmitToPerms;

        return requestVerification;
    }

    private converFollowUpDueDate(dueDate: Date) {
        return this.requestHelpersService.convertFollowupDueDateToLocal(!DateHelpers.isMinDate(dueDate) ? dueDate : null);
    }

    private getFollowupDueDateValidation(verificationStatus: RequestVerificationStatus) {
        const verificationStagesArray = (<UntypedFormArray>this.form?.get(['VerificationStages', 'Stages']));
        if (this.shouldDisplayFollowUpSection(verificationStatus) &&
            (this.requestItem.FollowupQfmRequired || this.requestItem.FollowupEvidenceRequired) &&
            !this.isBulkAction() &&
            (
                (
                    (verificationStatus === RequestVerificationStatus.APPROVED || 
                        verificationStatus === RequestVerificationStatus.SKIPPED) &&
                    this.currentStage?.Followup != 1 &&
                    this.areAllRemainingStagesSkipped(this.currentStage, verificationStagesArray)
                ) ||
                (verificationStatus === RequestVerificationStatus.REOPEN &&
                    this.form?.get('ReopenStageId').value === RequestReopenStageId.ReopenFollowupToUserStageId
                )
            )
        ) {
            return [Validators.required, CustomValidators.date];
        }

        return [];
    }

    private isFinalApproval() {
        const status = this.form.get('VerificationStatus').value;
        const hasFollowup = this.requestItem?.FollowupRequired;
        const isFollowup = this.currentStage?.Followup === 1;
        const verificationStagesArray = (<UntypedFormArray>this.form?.get(['VerificationStages', 'Stages']));
        const followupVerificationStagesArray = (<UntypedFormArray>this.form?.get(['FollowupVerificationStages', 'Stages']));

        if (status != RequestVerificationStatus.APPROVED && status != RequestVerificationStatus.SKIPPED) {
            return false;
        }

        if (isFollowup) {
            return this.areAllRemainingStagesSkipped(this.currentStage, followupVerificationStagesArray);
        } else {
            return !hasFollowup && this.areAllRemainingStagesSkipped(this.currentStage, verificationStagesArray);
        }
    }

    private areAllRemainingStagesSkipped(currentStage: ReqVerificationStage, verificationStagesArray: UntypedFormArray) {
        if (!currentStage || !verificationStagesArray) return false;
        
        return !verificationStagesArray.controls
            .find((stage) =>
                stage.get('Order').value > currentStage.Order && 
                stage.get('SelectedUserId').value !== TargetEvaluator.SKIP
            );
    }
}
